import { useState, useCallback, useEffect, ImgHTMLAttributes } from 'react';
import { usePlaceholderImage } from '@/hooks/usePlaceholderImage';

interface RetryImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Maximum retry attempts (default: 3) */
  maxRetries?: number;
  /** Delay between retries in ms (default: 1000, doubles each attempt) */
  retryDelay?: number;
  /** Fallback content when all retries fail */
  fallback?: React.ReactNode;
  /** Callback when all retries exhausted */
  onFinalError?: () => void;
}

/**
 * RetryImage Component
 *
 * A drop-in replacement for <img> that automatically retries loading on failure.
 * Implements exponential backoff and shows subtle visual feedback during retries.
 *
 * @example
 * ```tsx
 * <RetryImage
 *   src="https://example.com/image.jpg"
 *   alt="Example"
 *   maxRetries={3}
 *   fallback={<div>Custom fallback</div>}
 * />
 * ```
 */
export const RetryImage = ({
  src,
  alt,
  maxRetries = 3,
  retryDelay = 1000,
  fallback,
  onFinalError,
  onError,
  className = '',
  style,
  ...props
}: RetryImageProps) => {
  const placeholderImage = usePlaceholderImage();
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  // Reset state when src changes
  useEffect(() => {
    setRetryCount(0);
    setIsRetrying(false);
    setHasFailed(false);
    setCurrentSrc(src);
  }, [src]);

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Call original onError if provided
      onError?.(e);

      if (retryCount < maxRetries) {
        setIsRetrying(true);
        const delay = retryDelay * Math.pow(2, retryCount); // Exponential backoff

        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          // Force reload by clearing and resetting src
          setCurrentSrc('');
          requestAnimationFrame(() => {
            setCurrentSrc(src || '');
            setIsRetrying(false);
          });
        }, delay);
      } else {
        setHasFailed(true);
        onFinalError?.();
      }
    },
    [retryCount, maxRetries, retryDelay, src, onError, onFinalError]
  );

  // Show fallback if all retries exhausted
  if (hasFailed) {
    if (fallback) {
      return <>{fallback}</>;
    }
    // Default fallback: branding placeholder image (or gray SVG)
    return (
      <img
        src={placeholderImage}
        alt={alt || 'Image failed to load'}
        className={`bg-gray-100 object-contain ${className}`}
        style={style}
      />
    );
  }

  return (
    <img
      {...props}
      src={currentSrc}
      alt={alt}
      onError={handleError}
      className={`transition-opacity duration-200 ${className} ${isRetrying ? 'opacity-50' : 'opacity-100'}`}
      style={style}
    />
  );
};
